import type { ArticleVisibility } from "./article-visibility"

export type ArticleKind = "DOC" | "LINK"

export interface ArticleKindTarget {
	kind?: ArticleKind | null
	externalUrl?: string | null
}

// Rows created before the column existed have no `kind`, so absence means DOC.
export const isLinkArticle = (article: ArticleKindTarget) =>
	article.kind === "LINK"

export const LINK_ARTICLE_ICON = "Link"

export const LINK_ARTICLE_OPTION = {
	label: "External link",
	description: "The AI agent and Assistant have access.",
}

export const UNTITLED_DOC_LABEL = "New article"
export const UNTITLED_LINK_LABEL = "New external link"

// A link has no body to write, so calling an untitled one "New article" points
// the reader at the wrong thing.
export const getUntitledArticleLabel = (article: ArticleKindTarget) =>
	isLinkArticle(article) ? UNTITLED_LINK_LABEL : UNTITLED_DOC_LABEL

export const getArticleDisplayTitle = (
	article: ArticleKindTarget & { title?: string | null },
	translatedTitle?: string | null,
) => {
	const title = translatedTitle?.trim() || article.title?.trim()
	return title || getUntitledArticleLabel(article)
}

// A link never renders on the docs site, so it has no business sitting in the
// public tree. It lives in the Agent section alongside the rest of the agent's
// browse-nothing knowledge.
export const isPublicTreeRow = (article: {
	visibility?: string | null
	kind?: ArticleKind | null
}) => (article.visibility ?? "PUBLIC") === "PUBLIC" && !isLinkArticle(article)

// A link has no body, so everything the editor hangs off the document is off:
// the Tiptap surface, version history, proposals, translations, cover, summary.
export const showsArticleEditor = (article: ArticleKindTarget) =>
	!isLinkArticle(article)

export const showsArticleVersions = (article: ArticleKindTarget) =>
	!isLinkArticle(article)

// A public link is a nav entry on the docs site, so it still goes live. The
// word "Publish" fits; "Save" would understate that customers start seeing it.
export const getLinkGoLiveLabel = (visibility: ArticleVisibility) =>
	visibility === "PUBLIC" ? "Publish" : "Save"

// Host plus path, no scheme and no trailing slash: what a person recognises at
// a glance in a tile or a row.
export function displayUrl(url: string): string {
	try {
		const parsed = new URL(url)
		const path = parsed.pathname === "/" ? "" : parsed.pathname
		return `${parsed.host}${path}${parsed.search}`.replace(/\/$/, "")
	} catch {
		return url
	}
}

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"])

// Accepts what someone pastes: bare hostnames get https, anything that isn't a
// browsable http(s) address is rejected so it never reaches the fetch tool.
export function normalizeExternalUrl(input: string): string | null {
	const trimmed = input.trim()
	if (!trimmed) return null

	const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
		? trimmed
		: `https://${trimmed}`

	try {
		const url = new URL(candidate)
		if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null
		if (!url.hostname.includes(".")) return null
		return url.toString()
	} catch {
		return null
	}
}
